import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  LayoutDashboard,
  BookOpen,
  Video,
  Film,
  ClipboardList,
  LogOut,
  X,
  GraduationCap,
  Megaphone,
  MessagesSquare,
} from "lucide-react";
import { logoutUserThunk } from "../../../auth/authThunk";
import "../lms.css";

const BASE_NAV = [
  {
    section: "MAIN MENU",
    items: [
      { label: "Dashboard",    icon: LayoutDashboard, to: "/teacher-lms",             countKey: null, badge: null },
      { label: "My Courses",   icon: BookOpen,        to: "/teacher-lms/courses",      countKey: null, badge: null },
      { label: "Live Classes", icon: Video,           to: "/teacher-lms/live-classes",  countKey: null, badge: null },
      { label: "Recordings",   icon: Film,            to: "/teacher-lms/recordings",     countKey: null, badge: null },
      { label: "Audit Log",    icon: ClipboardList,   to: "/teacher-lms/audit-log",     countKey: null, badge: null },
      { label: "Attendance",   icon: ClipboardList,   to: "/teacher-lms/attendance",    countKey: null, badge: null },
      { label: "Exams",        icon: ClipboardList,   to: "/teacher-lms/exams",         countKey: null, badge: null },
      {
        label: "Communications",
        icon: MessagesSquare,
        children: [
          { label: "Notice Board/Chat", icon: Megaphone, to: "/teacher-lms/notice-board" },
        ],
      },
    ],
  },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function TeacherLmsSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleLogout = async () => {
    await dispatch(logoutUserThunk());
    localStorage.removeItem("userData");
    localStorage.removeItem("userToken");
    navigate("/login");
  };

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();

  const userName   = userData?.admin_name || userData?.name || "Teacher User";
  const schoolName = userData?.school_name || "";
  const userRole   = "Teacher";

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside className={`lms-sidebar ${isOpen ? "lms-sidebar--open" : ""}`}>
      <div className="lms-sidebar-logo">
        <div className="lms-logo-icon">D</div>
        <div className="lms-logo-text">
          <span className="lms-logo-title">DocMaster LMS</span>
          <span className="lms-logo-sub">TEACHER</span>
        </div>
        <button className="lms-sidebar-close" onClick={onClose} title="Close menu">
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      <nav className="lms-sidebar-nav">
        {BASE_NAV.map((sec) => (
          <div key={sec.section}>
            <div className="lms-nav-section-label">{sec.section}</div>
            {sec.items.map((item) => {
              const Icon = item.icon;
              const isActive = item.to === "/teacher-lms"
                ? location.pathname === "/teacher-lms" || location.pathname === "/teacher-lms/"
                : location.pathname.startsWith(item.to);

              if (item.children) {
                return (
                  <div key={item.label}>
                    <div className="lms-nav-item" style={{ cursor: "default", opacity: 0.85 }}>
                      <Icon className="lms-nav-icon" size={17} strokeWidth={2} />
                      <span className="lms-nav-label">{item.label}</span>
                    </div>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={handleNavClick}
                          className={({ isActive }) =>
                            `lms-nav-item ${isActive ? "lms-nav-item--active" : ""}`
                          }
                          style={{ paddingLeft: "34px" }}
                        >
                          <ChildIcon className="lms-nav-icon" size={15} strokeWidth={2} />
                          <span className="lms-nav-label">{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }

              const isIndexActive = item.to === "/teacher-lms" && location.pathname === "/teacher-lms";

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/teacher-lms"}
                  onClick={handleNavClick}
                  className={`lms-nav-item ${isActive ? "active" : ""}`}
                >
                  <span className="lms-nav-icon">
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className="lms-nav-label">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="lms-sidebar-user">
        <div className="lms-user-avatar">{getInitials(userName)}</div>
        <div className="lms-user-info">
          <div className="lms-user-name">{userName}</div>
          <div className="lms-user-role">{userRole}</div>
          {schoolName && (
            <div className="lms-user-school" title={schoolName}>{schoolName}</div>
          )}
        </div>
        <button className="lms-logout-btn" onClick={handleLogout} title="Logout">
          <LogOut size={15} strokeWidth={2} />
        </button>
      </div>
    </aside>
  );
}
